import { type MouseEventHandler, type ReactNode, useState } from 'react';

import Button, { type BaseProps } from 'components/Button';
import { PartialPopover } from 'components/Popover';

import type { ValidPopoverLink, ValidSitecoreLink } from './helpers';

interface Props extends BaseProps {
	children?: ReactNode;
	link: ValidSitecoreLink;
	onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
}

function WithPopover({
	children,
	link,
	onClick,
	...attrs
}: Props & { link: ValidPopoverLink }) {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<>
			<Button
				{...attrs}
				onClick={(e) => {
					setIsOpen(true);
					onClick?.(e);
				}}
				aria-haspopup="dialog"
			>
				{children}
			</Button>
			<PartialPopover
				isOpen={isOpen}
				partial={link.href}
				heading={link.title}
				onClose={() => setIsOpen(false)}
			/>
		</>
	);
}
WithPopover.displayName = 'ScButton_WithPopover';

export default function ScButton({ children, link, onClick, ...attrs }: Props) {
	const content = children ?? link.text;
	if (link.linktype === 'popover') {
		return (
			<WithPopover {...attrs} link={link}>
				{content}
			</WithPopover>
		);
	}
	return (
		<Button {...attrs} href={link.href} target={link.target} onClick={onClick}>
			{content}
		</Button>
	);
}

ScButton.displayName = 'ScButton';
